import EmvTerminal from '@ticketmaster/tm1pos-web-shared/components/EmvTerminal';
import LoadingComponent from '@ticketmaster/tm1pos-web-shared/components/LoadingComponent';
import { selectFeatureFlags } from '@ticketmaster/tm1pos-web-shared/feature-flags';
import {
  cancelEmvTransaction,
  clearEmvTransaction,
} from '@ticketmaster/tm1pos-web-shared/payment/emvPaymentModule-slice';
import classNames from 'classnames';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearCartLocation } from '../../middleware/mixpanel-constants';
import { clearCart } from '../App/actions';
import { deselectPlaceToUnReserve } from '../EventDetailPage/actions';
import { selectCheckoutState } from './checkout-selector';
import { CheckoutHeader } from './components/checkout-header/checkout-header';
import { CheckoutSummary } from './components/checkout-summary/checkout-summary';
import EmptyCart from './components/empty-cart/empty-cart';
import { checkout as legacyCheckout } from './components/form/actions';
import Form from './components/form/checkout-form';
import OrderConfirmation from './components/order-confirmation/order-confirmation';
import { PurchaseLoadingGear } from './components/purchase-loading-gear/purchase-loading-gear';
import { useCheckoutRouter } from './hooks/use-checkout-router';
import messages from './messages';
import './styles.scss';

type CheckoutProps = {
  initCartComponent: boolean;
  showCart: boolean;
  handleClickCart: () => void;
  continueShopping: () => void;
};

export const Checkout = ({ initCartComponent, showCart, handleClickCart, continueShopping }: CheckoutProps) => {
  const dispatch = useDispatch();
  const {
    order,
    checkoutSummaryData,
    isCheckoutInProgress,
    isOrderUnpaid,
    isDeselectingSeat,
    removingSeatPlacedId,
    checkoutCart,
    ifShowEMVLoading,
    isEmvOperationInProgress,
    isEmvCancelInProgress,
    isOrderDeleteInProgress,
    isEmvReverseInProgress,
    isEmvPaymentStartInProgress,
  } = useSelector(selectCheckoutState);

  const onClearCart = useCallback(() => {
    const clearPlace = clearCartLocation.CART;
    dispatch(clearCart(clearPlace));
  }, [dispatch]);

  const flags = useSelector(selectFeatureFlags);
  const { checkout } = useCheckoutRouter();
  const onFormSubmit = useCallback(async () => {
    dispatch(clearEmvTransaction());
    if (flags.checkoutRouter) {
      await checkout();
    } else {
      dispatch(legacyCheckout());
    }
  }, [dispatch, checkout, legacyCheckout]);

  const onCancelEmvPayment = useCallback(() => {
    // @ts-expect-error PayloadAction<StartEmvTransactionAction> is required but not used
    dispatch(cancelEmvTransaction());
  }, [dispatch]);

  const onDeselectSeat = useCallback(
    (placeId: string, generalAdmission: boolean, section: string, isBestAvailable: boolean) => {
      dispatch(deselectPlaceToUnReserve({ place: { placeId, generalAdmission, section, isBestAvailable } }));
    },
    [dispatch],
  );

  const checkoutHeader = useMemo(() => <CheckoutHeader handleClickCart={handleClickCart} />, [handleClickCart]);

  const checkoutContent = useMemo(() => {
    if (!order && !checkoutSummaryData) {
      return <EmptyCart btnHandler={handleClickCart} />;
    }

    return (
      <div className="sdr-checkout__content">
        <CheckoutSummary
          isDeselectingSeat={isDeselectingSeat}
          {...checkoutSummaryData}
          removingSeatPlacedId={removingSeatPlacedId}
          isOrderProcessed={!!order}
          onClearCart={onClearCart}
          isOrderUnpaid={isOrderUnpaid}
          onDeselectSeat={onDeselectSeat}
          checkoutSeatBlocks={checkoutCart}
          headerMsg={order ? messages.orderConfirmationHeader : messages.orderSummaryHeader}
        />
        {order ? (
          // @ts-expect-error OrderConfirmation is not typed yet
          <OrderConfirmation order={order} continueShopping={continueShopping} />
        ) : (
          <Form onSubmit={onFormSubmit} />
        )}
      </div>
    );
  }, [
    order,
    checkoutSummaryData,
    isDeselectingSeat,
    removingSeatPlacedId,
    checkoutCart,
    isOrderUnpaid,
    continueShopping,
    handleClickCart,
    onFormSubmit,
    onClearCart,
    onDeselectSeat,
  ]);

  const loadingGear = useMemo(() => {
    if (ifShowEMVLoading) {
      return <EmvTerminal onCancel={onCancelEmvPayment} cancelReady={!isEmvPaymentStartInProgress} />;
    }

    if (isEmvReverseInProgress) {
      return (
        <PurchaseLoadingGear
          message={messages.revertingTransaction}
          description={messages.revertingTransactionDescription}
        />
      );
    }

    if (isOrderDeleteInProgress) {
      return <PurchaseLoadingGear message={messages.cancelingOrder} description={messages.cancelingOrderDescription} />;
    }

    if (isCheckoutInProgress || isEmvOperationInProgress || isEmvCancelInProgress) {
      const loadingMessage = !isEmvCancelInProgress ? messages.processingOrder : messages.cancellingPayment;
      return <PurchaseLoadingGear message={loadingMessage} />;
    }
    return null;
  }, [
    ifShowEMVLoading,
    isEmvPaymentStartInProgress,
    isEmvReverseInProgress,
    isOrderDeleteInProgress,
    isCheckoutInProgress,
    isEmvOperationInProgress,
    isEmvCancelInProgress,
    onCancelEmvPayment,
  ]);

  const deletingGear = useMemo(() => (isDeselectingSeat ? <LoadingComponent largeGear /> : null), [isDeselectingSeat]);

  const innerCheckoutContent = useMemo(
    () => (
      <div className="sdr-checkout__inner-wrapper">
        {checkoutHeader}
        {checkoutContent}
      </div>
    ),
    [checkoutHeader, checkoutContent],
  );

  const checkoutClassName = classNames('sdr-checkout', {
    fastest: true,
    animated: true,
    slideInRight: initCartComponent && showCart,
    slideOutRight: initCartComponent && !showCart,
    'sdr-checkout--detailed': !!checkoutSummaryData,
    'sdr-checkout--hidden': !initCartComponent && !showCart,
  });

  return (
    <div className={checkoutClassName}>
      {loadingGear}
      {deletingGear}
      {innerCheckoutContent}
    </div>
  );
};

export default Checkout;
